// 供应商商品目录 — card-api 直充/卡密商品的只读镜像 (cardapi-catalog-sync task 5).
// 运营自助入口: 「立即同步」从供应商拉两池入库 (软下架失效行), 找到商品后「配成 SKU」
// 跳「商业 SKU」页预填履约参数 (product_code / 面值 / 结算价 / provider).
//
// 后端: cmd/admin
//   POST /admin/v1/cardapi/catalog/sync
//   GET  /admin/v1/cardapi/catalog?category=&enabled=&page=&size=

import { useState } from 'react';
import { Space, Tabs, Tag, Tooltip, Typography, message } from 'antd';
import { QuestionCircleOutlined, SyncOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ResourceList } from '@/components/ResourceList';
import { listCatalog, syncCatalog, type SupplierProduct } from '@/api/catalog';
import { formatDateTime } from '@/utils/format';

type CategoryTab = 'all' | 'topup' | 'card';

const CATEGORY_META: Record<'topup' | 'card', { label: string; color: string }> = {
  topup: { label: '直充', color: 'blue' },
  card: { label: '卡密', color: 'green' },
};

const CARD_KIND_META: Record<'H' | 'Y', { label: string; color: string }> = {
  H: { label: '官方卡', color: 'geekblue' },
  Y: { label: '自生成卡', color: 'orange' },
};

export function CatalogListPage() {
  const navigate = useNavigate();
  const [category, setCategory] = useState<CategoryTab>('all');
  const [syncing, setSyncing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await syncCatalog();
      message.success(
        `直充${r.topup_total}/卡密${r.card_total} 入库, 更新${r.upserted} 下架${r.disabled}`,
      );
      setReloadKey((k) => k + 1); // 刷新列表
    } catch (e) {
      // 后端 message 已含受限 IP / 限流原因, 直接透出.
      message.error(e instanceof Error ? e.message : '同步失败 (供应商限流或 IP 未加白)');
    } finally {
      setSyncing(false);
    }
  };

  // 「配成 SKU」: 跨页 router state 预填, 由「商业 SKU」页读取打开新建 Modal.
  const goConfigSKU = (row: SupplierProduct) => {
    navigate('/skus', {
      state: {
        prefillSKU: {
          product_code: row.product_code,
          category: row.category,
          face_value: row.face_value,
          settle_price: row.settle_price,
          provider: row.category === 'topup' ? 'card-api-topup' : 'card-api',
        },
      },
    });
  };

  return (
    <>
      <Tabs
        activeKey={category}
        onChange={(k) => setCategory(k as CategoryTab)}
        items={[
          { key: 'all', label: '全部' },
          { key: 'topup', label: '直充' },
          { key: 'card', label: '卡密' },
        ]}
      />

      <ResourceList<SupplierProduct>
        // category tab 切换 / 同步后 remount 触发 fetcher 重新拉取.
        key={`${category}:${reloadKey}`}
        title="供应商商品目录"
        description="card-api 供应商授权的直充 / 卡密商品目录 (只读镜像). 「立即同步」拉两池入库并软下架失效商品 — 供应商限流 10s/次, 请勿频繁点. 找到商品后「配成 SKU」跳「商业 SKU」页预填履约参数 (面值 / 结算价为原样字符串, 勿做金额运算)."
        rowKey="id"
        pagination="offset"
        actions={[
          {
            label: '立即同步',
            icon: <SyncOutlined />,
            onClick: () => void handleSync(),
            loading: syncing,
            disabled: syncing,
          },
        ]}
        filters={[
          {
            key: 'enabled',
            label: '状态',
            type: 'select',
            options: [
              { value: 'true', label: '在售' },
              { value: 'false', label: '已下架' },
            ],
          },
        ]}
        fetcher={async ({ filters, page, pageSize }) => {
          const enabled =
            filters.enabled === undefined ? undefined : filters.enabled === 'true';
          return listCatalog({
            category: category === 'all' ? undefined : category,
            enabled,
            page,
            size: pageSize,
          });
        }}
        columns={[
          { title: '名称', dataIndex: 'name' },
          {
            title: '分类',
            dataIndex: 'category',
            width: 90,
            render: (v: 'topup' | 'card') => (
              <Tag color={CATEGORY_META[v]?.color}>{CATEGORY_META[v]?.label ?? v}</Tag>
            ),
          },
          {
            title: '卡种',
            dataIndex: 'card_kind',
            width: 110,
            render: (v: 'H' | 'Y' | '') => {
              const meta = v ? CARD_KIND_META[v] : undefined;
              return meta ? (
                <Tag color={meta.color}>{meta.label}</Tag>
              ) : (
                <Typography.Text type="secondary">-</Typography.Text>
              );
            },
          },
          {
            title: '面值',
            dataIndex: 'face_value',
            align: 'right',
            render: (v: string) => v || <Typography.Text type="secondary">-</Typography.Text>,
          },
          {
            title: (
              <Tooltip title="直充口径 = 折后价; 卡密口径 = 卡种价 (H 官方卡 / Y 自生成卡)">
                <span>
                  结算价 <QuestionCircleOutlined style={{ color: '#999' }} />
                </span>
              </Tooltip>
            ),
            dataIndex: 'settle_price',
            align: 'right',
            render: (v: string | null) =>
              v ? <Typography.Text strong>{v}</Typography.Text> : <Typography.Text type="secondary">-</Typography.Text>,
          },
          {
            title: '折扣',
            dataIndex: 'discount',
            align: 'right',
            render: (v: string | null) =>
              v ? v : <Typography.Text type="secondary">-</Typography.Text>,
          },
          {
            title: '状态',
            dataIndex: 'enabled',
            width: 90,
            render: (v: boolean) =>
              v ? <Tag color="green">在售</Tag> : <Tag>已下架</Tag>,
          },
          {
            title: '同步时间',
            dataIndex: 'synced_at',
            render: (v: string) => formatDateTime(v),
          },
          {
            title: '操作',
            key: 'actions',
            width: 110,
            render: (_, row) => (
              <Space>
                <Typography.Link onClick={() => goConfigSKU(row)}>配成 SKU</Typography.Link>
              </Space>
            ),
          },
        ]}
      />
    </>
  );
}
